// The portal's API calls, through the shared client in @higgins/utils.
import { createApiClient } from '@higgins/utils';
import { getAccessToken } from './msal';

export type AppStatus = 'dev' | 'uat' | 'live' | 'retired';

interface PortalAppTile {
  key: string;
  name: string;
  description: string;
  url: string;
  status: AppStatus;
  icon: string;
  colour: string;
  stagingSite: boolean;
}

export interface MeResponse {
  name: string;
  upn: string;
  apps: PortalAppTile[];
  /** True for HigginsApps-IT and HigginsApps-Dev — shows the settings gear. */
  canManage: boolean;
  /** True when the live list couldn't be read and the built-in (possibly stale) copy was used. */
  usingFallback: boolean;
}

/** The fields an admin edits on the Add / Edit form. */
export interface TileFields {
  name: string;
  description: string;
  url: string;
  icon: string;
  colour: string;
  sortOrder: number;
  /** A second copy of an app for testing; its tile goes under "Staging Sites". */
  stagingSite: boolean;
}

export interface CatalogueApp extends TileFields {
  key: string;
  status: AppStatus;
  /** Ticked team keys. */
  teams: string[];
  /** False while the app's own API still ignores this model — ticks control the tile only. */
  gateLinked: boolean;
}

export interface CatalogueResponse {
  apps: CatalogueApp[];
  teams: { key: string; label: string }[];
  /** False until the first Save seeds the Dataverse tables. */
  seeded: boolean;
}

// Headers, errors and the lost-reply handling come from the shared client (#312).
const { json: apiFetch } = createApiClient({ getToken: getAccessToken });

const sendJson = (method: string, body: unknown): RequestInit => ({ method, body: JSON.stringify(body) });

export const getMe = () => apiFetch<MeResponse>('/me');

export const getCatalogue = () => apiFetch<CatalogueResponse>('/catalogue');

/** Save every row's status and ticks. */
export async function putCatalogue(apps: Pick<CatalogueApp, 'key' | 'status' | 'teams'>[]): Promise<void> {
  await apiFetch('/catalogue', sendJson('PUT', { apps }));
}

/** Add an app. It starts in Dev with no ticks. */
export async function createApp(key: string, fields: TileFields): Promise<void> {
  await apiFetch('/catalogue/apps', sendJson('POST', { key, ...fields }));
}

export async function updateApp(key: string, fields: TileFields): Promise<void> {
  await apiFetch(`/catalogue/apps/${encodeURIComponent(key)}`, sendJson('PUT', fields));
}
